// frontend/src/pages/AdminPublicHomepage.js
// Build: 2026-06-07-public-homepage-manual-featured-items-v1
// Purpose: Admin UI for managing public homepage featured works and the Mystery250 Puzzle of the Week.
// Notes:
// - Featured Works can be linked to a NextScenes story or entered as a manual published-book/showcase card.
// - Puzzle of the Week is admin-managed through Public Homepage Manager.
// - The public site can read the selected weekly mystery from /api/public-homepage.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { TEXT } from "../i18n";

const SECTION_FEATURED_STORIES = "featuredStories";
const SECTION_WEEKLY_MYSTERY = "weeklyMystery";
const SECTION_FEATURED_READING = "featuredReading";
const PUBLIC_HOMEPAGE_LANGUAGE_STORAGE_KEY = "nextscenes.adminPublicHomepage.languageFilter";

function getStoredPublicHomepageLanguage() {
  if (typeof window === "undefined") return "en";

  try {
    const saved = window.localStorage.getItem(PUBLIC_HOMEPAGE_LANGUAGE_STORAGE_KEY);
    return saved === "fr" ? "fr" : "en";
  } catch {
    return "en";
  }
}

function saveStoredPublicHomepageLanguage(language) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      PUBLIC_HOMEPAGE_LANGUAGE_STORAGE_KEY,
      language === "fr" ? "fr" : "en"
    );
  } catch {}
}

const BADGE_OPTIONS = [
  { value: "featured", en: "Featured", fr: "À la une" },
  { value: "new", en: "New", fr: "Nouveau" },
  { value: "live", en: "Live", fr: "En ligne" },
  { value: "nowReading", en: "Now Reading", fr: "Lecture en cours" },
  { value: "comingSoon", en: "Coming Soon", fr: "Bientôt" },
  { value: "publishedAmazon", en: "Published on Amazon", fr: "Publié sur Amazon" },
  { value: "previewOnly", en: "Preview Only", fr: "Aperçu seulement" },
];


const DEVELOPMENT_STATUS_OPTIONS = [
  { value: "inDevelopment", en: "In Development", fr: "En développement" },
  { value: "activeOnNextScenes", en: "Active on NextScenes", fr: "Actif sur NextScenes" },
  { value: "showcaseOnly", en: "Preview on NextScenes", fr: "Aperçu sur NextScenes" },
  { value: "completedOnNextScenes", en: "Completed on NextScenes", fr: "Développement terminé" },
];

const PUBLICATION_STATUS_OPTIONS = [
  { value: "notPublished", en: "Not Yet Published", fr: "Non encore publié" },
  { value: "comingSoon", en: "Publication Coming Soon", fr: "Publication bientôt" },
  { value: "publishedAmazon", en: "Published on Amazon", fr: "Publié sur Amazon" },
  { value: "publishedElsewhere", en: "Published Elsewhere", fr: "Publié ailleurs" },
];

const LANGUAGE_OPTIONS = [
  { value: "en", en: "English", fr: "Anglais" },
  { value: "fr", en: "French", fr: "Français" },
];

const PUBLIC_SITE_ORIGIN = "https://www.nextscenes.org";

const SITE_IMAGE_OPTIONS = [
  { value: "/images/covers/ugo-et-son-amie-awa-fr-cover.jpg", en: "Ugo et son amie Awa", fr: "Ugo et son amie Awa" },
  { value: "/images/covers/ugo-et-son-amie-awa-fr-cover.webp", en: "Ugo et son amie Awa WebP", fr: "Ugo et son amie Awa WebP" },
  { value: "/images/covers/cover-reflections-of-the-wayfarer-en.jpg", en: "Reflections of the Wayfarer", fr: "Reflections of the Wayfarer" },
  { value: "/images/covers/reflections-of-the-wayfarer-en.webp", en: "Reflections of the Wayfarer WebP", fr: "Reflections of the Wayfarer WebP" },
  { value: "/images/covers/cover-reflexions-du-voyageur-fr.jpg", en: "Réflexions d’un Voyageur", fr: "Réflexions d’un Voyageur" },
  { value: "/images/covers/reflexions-du-voyageur-fr.webp", en: "Réflexions d’un Voyageur WebP", fr: "Réflexions d’un Voyageur WebP" },
  { value: "/images/covers/king-jar-lingo-evel-broda-cover.jpg", en: "King Jar Lingo and Evel Broda", fr: "Le conte du roi Jar Lingo et d’Evel Broda" },
  { value: "/images/covers/the_trials_of_tarllus_front_cover.webp", en: "The Trials of Tarllus", fr: "The Trials of Tarllus" },
  { value: "/images/covers/didie-cover.jpg", en: "Didie", fr: "Didie" },
  { value: "/images/covers/didie-cover_1.jpg", en: "Didie cover 1", fr: "Didie couverture 1" },
  { value: "/images/covers/didie-cover_2.jpg", en: "Didie cover 2", fr: "Didie couverture 2" },
  { value: "/images/covers/didie-cover_3.jpg", en: "Didie cover 3", fr: "Didie couverture 3" },
  { value: "/images/covers/Alternative_Didie_Cover.png", en: "Didie alternative cover", fr: "Didie couverture alternative" },
  { value: "/images/covers/action-3d-clean.webp", en: "Action 3D clean", fr: "Action 3D propre" },
  { value: "/images/covers/mystery-3d-clean.webp", en: "Mystery 3D clean", fr: "Mystery 3D propre" },
  { value: "/images/featured-reading/reading-wisdom.webp", en: "Reading wisdom image", fr: "Image sagesse de lecture" },
];

const EMPTY_STORY_FORM = {
  id: "",
  section: SECTION_FEATURED_STORIES,
  storyId: "",
  isManual: false,
  language: "en",
  displayTitle: "",
  displayAuthor: "",
  hook: "",
  cta: "Open story",
  badge: "featured",
  displayOrder: 1,
  isActive: true,
  coverOverrideUrl: "",
  developmentStatus: "inDevelopment",
  publicationStatus: "notPublished",
  publicationUrl: "",
  publicationCta: "",
};

const EMPTY_MYSTERY_FORM = {
  id: "",
  section: SECTION_WEEKLY_MYSTERY,
  mysteryId: "",
  language: "en",
  displayTitle: "",
  hook: "",
  cta: "Try this week's puzzle",
  badge: "featured",
  displayOrder: 1,
  isActive: true,
  coverOverrideUrl: "",
};

const FEATURED_READING_DEFAULTS = {
  en: {
    displayTitle: "Anna Karenina",
    displayAuthor: "Leo Tolstoy",
    hook: "A searching novel of love, marriage, society, duty, and the price of choices made against the heart.",
    cta: "Explore the recommendation",
    coverOverrideUrl: "/images/featured-reading/reading-wisdom.webp",
    wisdomText: "Reading widens the mind, sharpens judgment, and lets us borrow wisdom from lives beyond our own.",
  },
  fr: {
    displayTitle: "Les Misérables",
    displayAuthor: "Victor Hugo",
    hook: "Un grand roman sur la justice, la misère, la compassion, la faute, le pardon et la dignité humaine.",
    cta: "Découvrir la recommandation",
    coverOverrideUrl: "/images/featured-reading/reading-wisdom.webp",
    wisdomText: "La lecture élargit l’esprit, affine le jugement et nous permet d’emprunter la sagesse de vies au-delà de la nôtre.",
  },
};

const EMPTY_READING_FORM = {
  id: "",
  section: SECTION_FEATURED_READING,
  language: "en",
  displayTitle: FEATURED_READING_DEFAULTS.en.displayTitle,
  displayAuthor: FEATURED_READING_DEFAULTS.en.displayAuthor,
  hook: FEATURED_READING_DEFAULTS.en.hook,
  cta: FEATURED_READING_DEFAULTS.en.cta,
  publicationUrl: "",
  coverOverrideUrl: FEATURED_READING_DEFAULTS.en.coverOverrideUrl,
  wisdomText: FEATURED_READING_DEFAULTS.en.wisdomText,
  displayOrder: 1,
  isActive: true,
};

function safeText(value, fallback = "") {
  const s = String(value || "").trim();
  return s || fallback;
}

function pickText(lang, en, fr) {
  return String(lang || "en") === "fr" ? fr : en;
}

function makePublicSiteImageUrl(value) {
  const s = safeText(value);
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return encodeURI(s);
  if (s.startsWith("/")) return `${PUBLIC_SITE_ORIGIN}${encodeURI(s)}`;
  return encodeURI(s);
}

function imageChoiceValueFromUrl(value) {
  const s = safeText(value);
  if (!s) return "";

  try {
    const url = new URL(s, PUBLIC_SITE_ORIGIN);
    if (url.hostname === "nextscenes.org" || url.hostname === "www.nextscenes.org") {
      return decodeURI(url.pathname);
    }
  } catch {}

  return s.startsWith("/") ? decodeURI(s) : "";
}

function makeReaderUrl(storyId) {
  const sid = String(storyId || "").trim();
  if (!sid) return "";
  return `https://app.nextscenes.org/reader-view?storyId=${encodeURIComponent(sid)}`;
}

function makeMysteryUrl(item) {
  const ref =
    safeText(item?.mystery?.slug) ||
    safeText(item?.ref) ||
    safeText(item?.mysteryId) ||
    safeText(item?.mystery?.id);
  if (!ref) return "";
  return `https://app.nextscenes.org/mystery250?mystery=${encodeURIComponent(ref)}`;
}

function normalizeItems(data) {
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

function normalizeMysteries(data) {
  if (Array.isArray(data?.mysteries)) return data.mysteries;
  return [];
}

function storyIdOf(story) {
  return safeText(story?.id || story?._id || story?.storyId);
}

function storyLanguageOf(story, fallback = "en") {
  const raw = String(
    story?.language || story?.storyLanguage || story?.lang || fallback || "en"
  )
    .trim()
    .toLowerCase();

  return raw === "fr" || raw === "french" || raw === "français" || raw === "francais" ? "fr" : "en";
}

function normalizeStory(story) {
  if (!story || typeof story !== "object") return null;

  const id = storyIdOf(story);
  if (!id) return null;

  return {
    ...story,
    id,
    title: safeText(story?.title, "Untitled Story"),
    author: safeText(story?.author),
    coverImageUrl: safeText(story?.coverImageUrl),
    description: safeText(story?.description),
    synopsis: safeText(story?.synopsis),
    language: storyLanguageOf(story, story?.language || "en"),
    visibility: story?.visibility || "",
    status: story?.status || "",
    updatedAt: story?.updatedAt || "",
  };
}

function dedupeStories(list) {
  const seen = new Set();
  const out = [];

  (Array.isArray(list) ? list : []).forEach((story) => {
    const normalized = normalizeStory(story);
    if (!normalized?.id) return;

    const key = String(normalized.id);
    if (seen.has(key)) return;

    seen.add(key);
    out.push(normalized);
  });

  return out;
}

function sortStoriesForLanguage(list, language) {
  const target = language === "fr" ? "fr" : language === "en" ? "en" : "";

  return dedupeStories(list).sort((a, b) => {
    if (target) {
      const aMatch = storyLanguageOf(a) === target ? 1 : 0;
      const bMatch = storyLanguageOf(b) === target ? 1 : 0;
      if (aMatch !== bMatch) return bMatch - aMatch;
    }

    return safeText(a.title).localeCompare(safeText(b.title), undefined, {
      sensitivity: "base",
    });
  });
}

function normalizeStories(data) {
  const raw = Array.isArray(data?.stories)
    ? data.stories
    : Array.isArray(data?.storylines)
      ? data.storylines
      : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
          ? data
          : [];

  return dedupeStories(raw);
}

function getStoryCover(story) {
  return safeText(story?.coverImageUrl) || "/images/default-covers/storyline-en.webp";
}

function getItemCover(item) {
  return (
    safeText(item?.coverOverrideUrl) ||
    safeText(item?.story?.coverImageUrl) ||
    safeText(item?.mystery?.imageUrl) ||
    "/images/default-covers/storyline-en.webp"
  );
}

function labelForBadge(value, lang) {
  const opt = BADGE_OPTIONS.find((x) => x.value === value);
  if (!opt) return value || "";
  return pickText(lang, opt.en, opt.fr);
}

function labelForLanguage(value, lang) {
  const opt = LANGUAGE_OPTIONS.find((x) => x.value === value);
  if (!opt) return value || "";
  return pickText(lang, opt.en, opt.fr);
}

function storyFromItem(item) {
  const story = item?.story || null;
  const storyId = item?.storyId || storyIdOf(story);
  if (!story || !storyId) return null;

  return normalizeStory({
    ...story,
    id: String(storyId),
    title: story.title || item.displayTitle || "Untitled Story",
    author: story.author || item.displayAuthor || "",
    coverImageUrl: story.coverImageUrl || "",
    description: story.description || "",
    synopsis: story.synopsis || "",
    language: story.language || item.language || "en",
    visibility: story.visibility || "",
    status: story.status || "",
    updatedAt: story.updatedAt || "",
  });
}

function mergeStoryIntoList(list, story) {
  const normalized = normalizeStory(story);
  if (!normalized?.id) return list;

  const exists = (Array.isArray(list) ? list : []).some(
    (x) => String(storyIdOf(x)) === String(normalized.id)
  );

  if (exists) return list;
  return [normalized, ...list];
}

function shortQuestion(value) {
  const s = safeText(value);
  if (!s) return "";
  const first = s.split(/\n+/).map((x) => x.trim()).find(Boolean) || s;
  return first.slice(0, 300);
}

function smallButtonStyle(extra = {}) {
  return {
    border: "1px solid rgba(15, 36, 24, 0.16)",
    background: "#ffffff",
    color: "rgba(15, 36, 24, 0.86)",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
    ...extra,
  };
}

function fieldStyle() {
  return {
    width: "100%",
    border: "1px solid rgba(15, 36, 24, 0.14)",
    borderRadius: 14,
    padding: "11px 12px",
    fontSize: 14,
    color: "rgba(15, 36, 24, 0.92)",
    background: "#ffffff",
    outline: "none",
    boxSizing: "border-box",
  };
}

function labelStyle() {
  return {
    display: "grid",
    gap: 6,
    fontSize: 12,
    fontWeight: 900,
    color: "rgba(15, 36, 24, 0.68)",
    letterSpacing: "0.03em",
    textTransform: "uppercase",
  };
}

function panelStyle(extra = {}) {
  return {
    border: "1px solid rgba(15, 36, 24, 0.1)",
    borderRadius: 24,
    background: "#ffffff",
    boxShadow: "0 16px 40px rgba(23, 31, 25, 0.06)",
    padding: 18,
    ...extra,
  };
}

function noticeStyle(type = "ok") {
  const bad = type === "bad";
  return {
    border: bad ? "1px solid rgba(185, 28, 28, 0.18)" : "1px solid rgba(20, 138, 74, 0.18)",
    background: bad ? "rgba(185, 28, 28, 0.07)" : "rgba(20, 138, 74, 0.08)",
    color: bad ? "#991b1b" : "#12683b",
    borderRadius: 16,
    padding: "10px 12px",
    fontWeight: 800,
    marginBottom: 12,
  };
}

function CoverImagePicker({ lang = "en", value = "", onChange, copy, uploadMode = "cover" }) {
  const selectedValue = imageChoiceValueFromUrl(value);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const hasImage = Boolean(safeText(value));

  async function uploadSelectedFile(event) {
    const file = event?.target?.files?.[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "image/webp"];
    const type = String(file.type || "").toLowerCase();

    if (!allowed.includes(type) || Number(file.size || 0) > 5 * 1024 * 1024) {
      setUploadMessage(copy.coverUploadInvalid);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    try {
      setUploading(true);
      setUploadMessage(copy.coverUploading);

      const uploader = uploadMode === "mystery" ? api.uploadMysteryImage : api.uploadStoryCover;
      const result = await uploader(file);
      const uploadedUrl = safeText(result?.url || result?.publicUrl);
      if (!uploadedUrl) throw new Error(copy.coverUploadFailed);

      onChange(uploadedUrl);
      setUploadMessage(copy.coverUploadSuccess);
    } catch (err) {
      console.error("Public Homepage cover upload failed:", err);
      setUploadMessage(safeText(err?.message, copy.coverUploadFailed));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  return (
    <section
      style={{
        gridColumn: "1 / -1",
        display: "grid",
        gap: 12,
        border: "1px solid rgba(151, 116, 43, 0.18)",
        borderRadius: 18,
        background: "rgba(255,255,255,0.72)",
        padding: 14,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 900,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "#7b5316",
          }}
        >
          {copy.coverImageTitle}
        </div>
        <div style={{ marginTop: 4, fontSize: 12, color: "rgba(15,36,24,0.58)", lineHeight: 1.45 }}>
          {copy.coverImageHelp}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 8,
          alignItems: "end",
        }}
      >
        <label style={labelStyle()}>
          {copy.coverSelect}
          <select
            value={selectedValue}
            onChange={(e) => {
              onChange(makePublicSiteImageUrl(e.target.value));
              setUploadMessage("");
            }}
            style={fieldStyle()}
          >
            <option value="">{copy.coverSelectPlaceholder}</option>
            {SITE_IMAGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {pickText(lang, option.en, option.fr)}
              </option>
            ))}
          </select>
        </label>

        <div style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 900, color: "rgba(15, 36, 24, 0.68)", letterSpacing: "0.03em", textTransform: "uppercase" }}>
            {copy.coverUpload}
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
            onChange={uploadSelectedFile}
            disabled={uploading}
            style={{
              ...fieldStyle(),
              padding: "8px 10px",
              background: uploading ? "rgba(15,36,24,0.05)" : "#fff",
            }}
          />
        </div>

        <button
          type="button"
          onClick={() => {
            onChange("");
            setUploadMessage("");
          }}
          disabled={uploading || !hasImage}
          style={smallButtonStyle({
            borderRadius: 14,
            padding: "11px 12px",
            color: "#8a5b11",
            borderColor: "rgba(151, 116, 43, 0.26)",
            opacity: uploading || !hasImage ? 0.55 : 1,
          })}
        >
          {copy.clearImage}
        </button>
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: "rgba(15,36,24,0.52)", lineHeight: 1.45 }}>
        {uploadMessage || copy.coverUploadHelp}
      </div>

      {hasImage ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "68px minmax(0, 1fr)",
            gap: 10,
            alignItems: "center",
            border: "1px solid rgba(15,36,24,0.1)",
            borderRadius: 14,
            padding: 9,
            background: "rgba(248,250,248,0.88)",
          }}
        >
          <div
            style={{
              width: 68,
              height: 92,
              overflow: "hidden",
              borderRadius: 8,
              background: "rgba(15,36,24,0.08)",
              border: "1px solid rgba(15,36,24,0.1)",
            }}
          >
            <img src={value} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 900, color: "#12683b" }}>{copy.coverSelected}</div>
            <div style={{ marginTop: 4, fontSize: 11, color: "rgba(15,36,24,0.52)", overflowWrap: "anywhere" }}>
              {value}
            </div>
          </div>
        </div>
      ) : null}

      <details>
        <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 800, color: "#7b5316" }}>
          {copy.coverAdvanced}
        </summary>
        <label style={{ ...labelStyle(), marginTop: 10 }}>
          {copy.coverOverride}
          <input
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              setUploadMessage("");
            }}
            placeholder="https://... or /images/..."
            style={fieldStyle()}
          />
        </label>
      </details>
    </section>
  );
}

export default function AdminPublicHomepage({ lang = "en", stewardOnly = false }) {
  const storyFormRef = useRef(null);
  const mysteryFormRef = useRef(null);
  const readingFormRef = useRef(null);

  const copy = useMemo(() => {
    const t = TEXT?.[lang] || TEXT?.en || {};

    return {
      title:
        t.adminPublicHomepageTitle ||
        pickText(lang, "Public Homepage Manager", "Gestionnaire de la page publique"),
      subtitle: pickText(
        lang,
        "Choose what appears on the public homepage: featured reading, featured stories, and the weekly Mystery250 puzzle.",
        "Choisissez ce qui apparaît sur la page publique : lecture à l’honneur, histoires à la une et énigme Mystery250 de la semaine."
      ),
      featuredStories: pickText(lang, "Featured Stories", "Histoires à la une"),
      weeklyMystery: pickText(lang, "Mystery250 Puzzle of the Week", "Énigme Mystery250 de la semaine"),
      weeklyMysteryHelp: pickText(
        lang,
        "Select one Mystery250 puzzle for the public homepage. The public site will update automatically.",
        "Sélectionnez une énigme Mystery250 pour la page publique. Le site public se mettra à jour automatiquement."
      ),
      featuredReading: pickText(lang, "Featured Reading / Book of the Month", "Lecture à l’honneur / Livre du mois"),
      featuredReadingHelp: pickText(
        lang,
        "Set one monthly literary recommendation for each public language.",
        "Définissez une recommandation littéraire mensuelle pour chaque langue publique."
      ),
      setReading: pickText(lang, "Set featured reading", "Définir la lecture à l’honneur"),
      updateReading: pickText(lang, "Update featured reading", "Mettre à jour la lecture à l’honneur"),
      noReading: pickText(lang, "No featured reading has been set yet.", "Aucune lecture à l’honneur n’a encore été définie."),
      readingUrl: pickText(lang, "Optional link", "Lien facultatif"),
      readingCoverUrl: pickText(lang, "Reading image URL", "URL de l’image de lecture"),
      readingWisdomText: pickText(lang, "Reading wisdom sentence", "Phrase de sagesse sur la lecture"),
      addStory: pickText(lang, "Add featured story", "Ajouter une histoire à la une"),
      editStory: pickText(lang, "Edit featured story", "Modifier l’histoire à la une"),
      setMystery: pickText(lang, "Set weekly mystery", "Définir l’énigme hebdomadaire"),
      updateMystery: pickText(lang, "Update weekly mystery", "Mettre à jour l’énigme"),
      selectStory: pickText(lang, "Select story", "Sélectionner une histoire"),
      selectMystery: pickText(lang, "Select Mystery250 puzzle", "Sélectionner une énigme Mystery250"),
      searchMysteries: pickText(lang, "Search mysteries", "Rechercher des énigmes"),
      language: pickText(lang, "Language", "Langue"),
      displayTitle: pickText(lang, "Display title", "Titre affiché"),
      displayAuthor: pickText(lang, "Display author", "Auteur affiché"),
      hook: pickText(lang, "Short description / teaser", "Brève description / accroche"),
      cta: pickText(lang, "CTA text", "Texte du bouton"),
      badge: pickText(lang, "Badge", "Badge"),
      order: pickText(lang, "Order", "Ordre"),
      active: pickText(lang, "Active", "Actif"),
      coverOverride: pickText(lang, "Image URL", "URL de l’image"),
      coverImageTitle: pickText(lang, "Cover image", "Image de couverture"),
      coverImageHelp: pickText(
        lang,
        "Choose an existing image or upload one from your computer. The technical URL is available only under Advanced options.",
        "Choisissez une image existante ou téléversez-en une depuis votre ordinateur. L’URL technique se trouve uniquement dans les options avancées."
      ),
      coverSelect: pickText(lang, "Choose existing image", "Choisir une image existante"),
      coverSelectPlaceholder: pickText(lang, "Select an image already on the public site", "Sélectionner une image déjà sur le site public"),
      coverUpload: pickText(lang, "Upload from computer", "Téléverser depuis l’ordinateur"),
      coverUploading: pickText(lang, "Uploading image…", "Téléversement de l’image…"),
      coverUploadHelp: pickText(lang, "JPG, PNG or WebP, maximum 5 MB.", "JPG, PNG ou WebP, maximum 5 Mo."),
      coverUploadSuccess: pickText(
        lang,
        "Image uploaded and selected. Click Save or Update below to apply it to the homepage item.",
        "Image téléversée et sélectionnée. Cliquez sur Enregistrer ou Mettre à jour ci-dessous pour l’appliquer à l’élément de la page d’accueil."
      ),
      coverUploadInvalid: pickText(
        lang,
        "Choose a JPG, PNG or WebP image no larger than 5 MB.",
        "Choisissez une image JPG, PNG ou WebP ne dépassant pas 5 Mo."
      ),
      coverUploadFailed: pickText(lang, "Image upload failed.", "Échec du téléversement de l’image."),
      coverSelected: pickText(lang, "Selected image", "Image sélectionnée"),
      coverAdvanced: pickText(lang, "Advanced: enter an image URL", "Avancé : saisir une URL d’image"),
      clearImage: pickText(lang, "Clear image", "Effacer l’image"),
      developmentStatus: pickText(lang, "NextScenes status", "Statut NextScenes"),
      publicationStatus: pickText(lang, "Publication status", "Statut de publication"),
      publicationUrl: pickText(lang, "Publication URL", "URL de publication"),
      publicationCta: pickText(lang, "Publication button text", "Texte du bouton de publication"),
      save: pickText(lang, "Save", "Enregistrer"),
      update: pickText(lang, "Update", "Mettre à jour"),
      cancel: pickText(lang, "Cancel", "Annuler"),
      delete: pickText(lang, "Delete", "Supprimer"),
      editAction: t.edit || pickText(lang, "Edit", "Modifier"),
      refresh: t.refresh || pickText(lang, "Refresh", "Actualiser"),
      loading: pickText(lang, "Loading…", "Chargement…"),
      noItems: pickText(lang, "No featured homepage items yet.", "Aucune histoire à la une pour le moment."),
      noMystery: pickText(lang, "No weekly mystery has been selected yet.", "Aucune énigme hebdomadaire n’a encore été sélectionnée."),
      storySearch: pickText(lang, "Search stories", "Rechercher des histoires"),
      fillEmptyStory: pickText(lang, "Fill empty fields from selected story", "Remplir les champs vides depuis l’histoire"),
      fillEmptyMystery: pickText(lang, "Fill fields from selected mystery", "Remplir les champs depuis l’énigme"),
      saved: pickText(lang, "Homepage item saved.", "Élément de page publique enregistré."),
      deleted: pickText(lang, "Homepage item deleted.", "Élément de page publique supprimé."),
      loadFailed: pickText(lang, "Homepage items could not be loaded.", "Impossible de charger les éléments de la page publique."),
      saveFailed: pickText(lang, "Homepage item could not be saved.", "Impossible d’enregistrer l’élément de page publique."),
      selectStoryFirst: pickText(lang, "Please select a story first.", "Veuillez sélectionner une histoire d’abord."),
      manualCard: pickText(lang, "Manual published-book/showcase card", "Carte manuelle livre publié / vitrine"),
      manualCardHelp: pickText(
        lang,
        "Use this when the public homepage card should be controlled here but is not tied to an active NextScenes story.",
        "Utilisez ceci quand la carte publique doit être contrôlée ici sans être liée à une histoire active NextScenes."
      ),
      manualCardNeeds: pickText(
        lang,
        "Manual cards need a title, short description, and cover image URL.",
        "Les cartes manuelles nécessitent un titre, une courte description et une URL d’image de couverture."
      ),
      selectMysteryFirst: pickText(lang, "Please select a Mystery250 puzzle first.", "Veuillez sélectionner une énigme Mystery250 d’abord."),
      confirmDelete: pickText(lang, "Delete this homepage item?", "Supprimer cet élément de la page publique ?"),
      preview: pickText(lang, "Preview", "Aperçu"),
      itemStatusActive: pickText(lang, "Active", "Actif"),
      itemStatusInactive: pickText(lang, "Inactive", "Inactif"),
      manualHint: pickText(
        lang,
        "Custom text is preserved. The fill button only completes empty fields.",
        "Le texte personnalisé est conservé. Le bouton de remplissage complète seulement les champs vides."
      ),
      mysteryManualHint: pickText(
        lang,
        "Use the image URL from the selected Mystery250 puzzle, or paste a different image URL here.",
        "Utilisez l’URL de l’image de l’énigme sélectionnée, ou collez ici une autre URL d’image."
      ),
    };
  }, [lang]);

  const [items, setItems] = useState([]);
  const [weeklyItems, setWeeklyItems] = useState([]);
  const [readingItems, setReadingItems] = useState([]);
  const [stories, setStories] = useState([]);
  const [mysteries, setMysteries] = useState([]);
  const [languageFilter, setLanguageFilter] = useState(getStoredPublicHomepageLanguage);
  const [storyQuery, setStoryQuery] = useState("");
  const [mysteryQuery, setMysteryQuery] = useState("");
  const [form, setForm] = useState(EMPTY_STORY_FORM);
  const [mysteryForm, setMysteryForm] = useState(EMPTY_MYSTERY_FORM);
  const [readingForm, setReadingForm] = useState(EMPTY_READING_FORM);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingMystery, setIsEditingMystery] = useState(false);
  const [isEditingReading, setIsEditingReading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingMysteryItems, setLoadingMysteryItems] = useState(false);
  const [loadingReadingItems, setLoadingReadingItems] = useState(false);
  const [loadingStories, setLoadingStories] = useState(false);
  const [loadingMysteries, setLoadingMysteries] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingMystery, setSavingMystery] = useState(false);
  const [savingReading, setSavingReading] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  async function loadItems(nextLanguage = languageFilter) {
    setLoadingItems(true);
    setError("");
    try {
      const data = await api.getAdminPublicHomepageItems({
        language: nextLanguage || "",
        section: SECTION_FEATURED_STORIES,
      });
      setItems(normalizeItems(data));
    } catch (err) {
      setError(err?.message || copy.loadFailed);
    } finally {
      setLoadingItems(false);
    }
  }

  async function loadWeeklyItems(nextLanguage = languageFilter) {
    setLoadingMysteryItems(true);
    setError("");
    try {
      const data = await api.getAdminPublicHomepageItems({
        language: nextLanguage || "",
        section: SECTION_WEEKLY_MYSTERY,
      });
      const nextItems = normalizeItems(data);
      setWeeklyItems(nextItems);
      const active = nextItems.find((item) => item.isActive === true) || nextItems[0];
      if (active && !isEditingMystery && !mysteryForm.id) {
        setMysteryFormFromItem(active);
      }
    } catch (err) {
      setError(err?.message || copy.loadFailed);
    } finally {
      setLoadingMysteryItems(false);
    }
  }

  async function loadReadingItems(nextLanguage = languageFilter) {
    setLoadingReadingItems(true);
    setError("");
    try {
      const data = await api.getAdminPublicHomepageItems({
        language: nextLanguage || "",
        section: SECTION_FEATURED_READING,
      });
      const nextItems = normalizeItems(data).filter(
        (item) => item?.section === SECTION_FEATURED_READING
      );
      setReadingItems(nextItems);
      const active = nextItems.find((item) => item.isActive === true) || nextItems[0];

      /*
       * Keep the Featured Reading form aligned with the selected language.
       * The previous readingForm.id guard could preserve a stale English form
       * while loading French items, especially after language toggles or refreshes.
       */
      if (active && !isEditingReading) {
        setReadingFormFromItem(active);
      } else if (!active && !isEditingReading) {
        resetReadingForm(nextLanguage || languageFilter || "en");
      }
    } catch (err) {
      setError(err?.message || copy.loadFailed);
    } finally {
      setLoadingReadingItems(false);
    }
  }

  async function loadStories(opts = {}) {
    const q = opts.query !== undefined ? opts.query : storyQuery;
    const rawLanguage = opts.language !== undefined ? opts.language : form.language || languageFilter;
    const l = rawLanguage === "fr" ? "fr" : rawLanguage === "en" ? "en" : "";

    setLoadingStories(true);
    setError("");

    try {
      const data = await api.getAdminPublicHomepageStories({
        query: q || "",
        language: l || "",
        limit: 100,
      });

      let nextStories = normalizeStories(data);

      if (l) {
        const fallbackData = await api.getAdminPublicHomepageStories({
          query: q || "",
          language: "",
          limit: 100,
        });
        nextStories = dedupeStories([...nextStories, ...normalizeStories(fallbackData)]);
      }

      setStories(sortStoriesForLanguage(nextStories, l));
    } catch (err) {
      setError(err?.message || copy.loadFailed);
    } finally {
      setLoadingStories(false);
    }
  }

  async function loadMysteries(opts = {}) {
    const q = opts.query !== undefined ? opts.query : mysteryQuery;

    setLoadingMysteries(true);
    setError("");

    try {
      const data = await api.getAdminPublicHomepageMysteries({
        query: q || "",
        limit: 100,
      });

      setMysteries(normalizeMysteries(data));
    } catch (err) {
      setError(err?.message || copy.loadFailed);
    } finally {
      setLoadingMysteries(false);
    }
  }

  useEffect(() => {
    const initialLanguage = languageFilter === "fr" ? "fr" : "en";

    if (!stewardOnly) loadItems(initialLanguage);
    loadWeeklyItems(initialLanguage);
    loadReadingItems(initialLanguage);
    if (!stewardOnly) loadStories({ language: initialLanguage, query: "" });
    loadMysteries({ query: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function scrollToStoryForm() {
    window.setTimeout(() => {
      try {
        storyFormRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } catch {}
    }, 40);
  }

  function scrollToMysteryForm() {
    window.setTimeout(() => {
      try {
        mysteryFormRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } catch {}
    }, 40);
  }

  function scrollToReadingForm() {
    window.setTimeout(() => {
      try {
        readingFormRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } catch {}
    }, 40);
  }

  function resetForm(nextLanguage = languageFilter || "en") {
    setForm({
      ...EMPTY_STORY_FORM,
      language: nextLanguage || "en",
      cta: nextLanguage === "fr" ? "Lire" : "Open story",
      displayOrder: Math.max(1, (items?.length || 0) + 1),
    });
    setIsEditing(false);
    setNotice("");
    setError("");
  }

  function resetMysteryForm(nextLanguage = languageFilter || "en") {
    setMysteryForm({
      ...EMPTY_MYSTERY_FORM,
      language: nextLanguage || "en",
      cta: nextLanguage === "fr" ? "Essayez l’énigme de la semaine" : "Try this week's puzzle",
    });
    setIsEditingMystery(false);
    setNotice("");
    setError("");
  }

  function resetReadingForm(nextLanguage = languageFilter || "en") {
    const l = nextLanguage === "fr" ? "fr" : "en";
    const defaults = FEATURED_READING_DEFAULTS[l] || FEATURED_READING_DEFAULTS.en;

    setReadingForm({
      ...EMPTY_READING_FORM,
      language: l,
      displayTitle: defaults.displayTitle,
      displayAuthor: defaults.displayAuthor,
      hook: defaults.hook,
      cta: defaults.cta,
      publicationUrl: "",
      coverOverrideUrl: defaults.coverOverrideUrl,
      wisdomText: defaults.wisdomText,
    });
    setIsEditingReading(false);
    setNotice("");
    setError("");
  }

  function onLanguageFilterChange(nextLanguage) {
    const l = nextLanguage === "fr" ? "fr" : "en";
    setLanguageFilter(l);
    saveStoredPublicHomepageLanguage(l);
    resetForm(l);
    resetMysteryForm(l);
    resetReadingForm(l);
    if (!stewardOnly) loadItems(l);
    loadWeeklyItems(l);
    loadReadingItems(l);
    if (!stewardOnly) loadStories({ language: l, query: storyQuery });
  }

  function onFormLanguageChange(nextLanguage) {
    const l = nextLanguage === "fr" ? "fr" : "en";

    setForm((prev) => ({
      ...prev,
      language: l,
      storyId: prev.isManual ? "" : prev.storyId,
      cta:
        prev.cta && prev.cta !== "Open story" && prev.cta !== "Lire"
          ? prev.cta
          : l === "fr"
            ? "Lire"
            : "Open story",
    }));

    loadStories({ language: l, query: storyQuery });
  }

  function onReadingFormLanguageChange(nextLanguage) {
    const l = nextLanguage === "fr" ? "fr" : "en";
    const defaults = FEATURED_READING_DEFAULTS[l] || FEATURED_READING_DEFAULTS.en;

    setReadingForm((prev) => {
      const knownDefaultTitles = ["Anna Karenina", "Les Misérables"];
      const knownDefaultAuthors = ["Leo Tolstoy", "Victor Hugo"];
      const knownDefaultCtas = ["Explore the recommendation", "Découvrir la recommandation"];
      const knownDefaultCovers = [
        "/images/featured-reading/anna-karenina.webp",
        "/images/featured-reading/les-miserables.webp",
        "/images/featured-reading/reading-wisdom.webp",
      ];
      const knownDefaultWisdomTexts = [
        FEATURED_READING_DEFAULTS.en.wisdomText,
        FEATURED_READING_DEFAULTS.fr.wisdomText,
      ];

      return {
        ...prev,
        language: l,
        displayTitle:
          !prev.displayTitle || knownDefaultTitles.includes(prev.displayTitle)
            ? defaults.displayTitle
            : prev.displayTitle,
        displayAuthor:
          !prev.displayAuthor || knownDefaultAuthors.includes(prev.displayAuthor)
            ? defaults.displayAuthor
            : prev.displayAuthor,
        hook:
          !prev.hook ||
          prev.hook.includes("A searching novel of love") ||
          prev.hook.includes("Un grand roman sur la justice")
            ? defaults.hook
            : prev.hook,
        cta:
          !prev.cta || knownDefaultCtas.includes(prev.cta)
            ? defaults.cta
            : prev.cta,
        coverOverrideUrl:
          !prev.coverOverrideUrl || knownDefaultCovers.includes(prev.coverOverrideUrl)
            ? defaults.coverOverrideUrl
            : prev.coverOverrideUrl,
        wisdomText:
          !prev.wisdomText || knownDefaultWisdomTexts.includes(prev.wisdomText)
            ? defaults.wisdomText
            : prev.wisdomText,
      };
    });
  }

  function onMysteryFormLanguageChange(nextLanguage) {
    const l = nextLanguage === "fr" ? "fr" : "en";

    setMysteryForm((prev) => ({
      ...prev,
      language: l,
      cta:
        prev.cta &&
        prev.cta !== "Try this week's puzzle" &&
        prev.cta !== "Essayez l’énigme de la semaine"
          ? prev.cta
          : l === "fr"
            ? "Essayez l’énigme de la semaine"
            : "Try this week's puzzle",
    }));
  }

  function onSelectStory(storyId) {
    const story = stories.find((s) => String(s.id) === String(storyId));
    const storyLanguage = story?.language === "fr" ? "fr" : form.language || languageFilter || "en";

    setForm((prev) => ({
      ...prev,
      storyId,
      isManual: false,
      language: prev.language || storyLanguage,
      displayTitle: prev.displayTitle || safeText(story?.title),
      displayAuthor: prev.displayAuthor || safeText(story?.author),
      hook: prev.hook || safeText(story?.description) || safeText(story?.synopsis),
      cta: prev.cta || (storyLanguage === "fr" ? "Lire" : "Open story"),
    }));
  }

  function onSelectMystery(mysteryId) {
    const mystery = mysteries.find((m) => String(m.id) === String(mysteryId));

    setMysteryForm((prev) => ({
      ...prev,
      mysteryId,
      displayTitle: prev.displayTitle || safeText(mystery?.title),
      hook: prev.hook || safeText(mystery?.teaser) || shortQuestion(mystery?.question),
      coverOverrideUrl: prev.coverOverrideUrl || safeText(mystery?.imageUrl),
    }));
  }

  function fillEmptyFromStory() {
    if (form.isManual) {
      setError(copy.selectStoryFirst);
      return;
    }

    const story = stories.find((s) => String(s.id) === String(form.storyId));
    if (!story) {
      setError(copy.selectStoryFirst);
      return;
    }

    const storyLanguage = story?.language === "fr" ? "fr" : form.language || "en";

    setError("");
    setForm((prev) => ({
      ...prev,
      language: prev.language || storyLanguage,
      displayTitle: prev.displayTitle || safeText(story.title),
      displayAuthor: prev.displayAuthor || safeText(story.author),
      hook: prev.hook || safeText(story.description) || safeText(story.synopsis),
      cta: prev.cta || (storyLanguage === "fr" ? "Lire" : "Open story"),
      coverOverrideUrl: prev.coverOverrideUrl || "",
    }));
  }

  function fillFromMystery() {
    const mystery = mysteries.find((m) => String(m.id) === String(mysteryForm.mysteryId));
    if (!mystery) {
      setError(copy.selectMysteryFirst);
      return;
    }

    setError("");
    setMysteryForm((prev) => ({
      ...prev,
      displayTitle: safeText(mystery.title),
      hook: safeText(mystery.teaser) || shortQuestion(mystery.question),
      cta:
        prev.language === "fr"
          ? "Essayez l’énigme de la semaine"
          : "Try this week's puzzle",
      coverOverrideUrl: safeText(mystery.imageUrl),
    }));
  }

  function startEdit(item) {
    const itemStory = storyFromItem(item);

    setStories((prev) => mergeStoryIntoList(prev, itemStory));
    setIsEditing(true);
    setNotice("");
    setError("");

    setForm({
      id: item.id || "",
      section: SECTION_FEATURED_STORIES,
      storyId: item.storyId || item.story?.id || "",
      isManual: !(item.storyId || item.story?.id),
      language: item.language === "fr" ? "fr" : "en",
      displayTitle: item.displayTitle || item.story?.title || "",
      displayAuthor: item.displayAuthor || item.story?.author || "",
      hook: item.hook || "",
      cta: item.cta || (item.language === "fr" ? "Lire" : "Open story"),
      badge: item.badge || "featured",
      displayOrder: Number(item.displayOrder || 1),
      isActive: item.isActive === true,
      coverOverrideUrl: item.coverOverrideUrl || "",
      developmentStatus: item.developmentStatus || "inDevelopment",
      publicationStatus: item.publicationStatus || "notPublished",
      publicationUrl: item.publicationUrl || "",
      publicationCta: item.publicationCta || "",
    });

    scrollToStoryForm();
  }

  function setMysteryFormFromItem(item) {
    setMysteryForm({
      id: item.id || "",
      section: SECTION_WEEKLY_MYSTERY,
      mysteryId: item.mysteryId || item.mystery?.id || "",
      language: item.language === "fr" ? "fr" : "en",
      displayTitle: item.displayTitle || item.mystery?.title || "",
      hook: item.hook || item.mystery?.question || "",
      cta:
        item.cta ||
        (item.language === "fr" ? "Essayez l’énigme de la semaine" : "Try this week's puzzle"),
      badge: item.badge || "featured",
      displayOrder: Number(item.displayOrder || 1),
      isActive: item.isActive === true,
      coverOverrideUrl: item.coverOverrideUrl || item.mystery?.imageUrl || "",
    });
  }

  function startEditMystery(item) {
    setIsEditingMystery(true);
    setNotice("");
    setError("");
    setMysteryFormFromItem(item);
    scrollToMysteryForm();
  }

  function setReadingFormFromItem(item) {
    setReadingForm({
      id: item.id || "",
      section: SECTION_FEATURED_READING,
      language: item.language === "fr" ? "fr" : "en",
      displayTitle: item.displayTitle || "",
      displayAuthor: item.displayAuthor || "",
      hook: item.hook || "",
      cta:
        item.cta ||
        (item.language === "fr" ? "Découvrir la recommandation" : "Explore the recommendation"),
      publicationUrl: item.publicationUrl || "",
      coverOverrideUrl: item.coverOverrideUrl || "",
      wisdomText:
        item.wisdomText ||
        (item.language === "fr"
          ? FEATURED_READING_DEFAULTS.fr.wisdomText
          : FEATURED_READING_DEFAULTS.en.wisdomText),
      displayOrder: Number(item.displayOrder || 1),
      isActive: item.isActive === true,
    });
  }

  function startEditReading(item) {
    setIsEditingReading(true);
    setNotice("");
    setError("");
    setReadingFormFromItem(item);
    scrollToReadingForm();
  }

  async function saveReadingItem(e) {
    e.preventDefault();
    setNotice("");
    setError("");

    if (!safeText(readingForm.displayTitle) || !safeText(readingForm.displayAuthor) || !safeText(readingForm.hook)) {
      setError(
        pickText(
          lang,
          "Please provide a title, author, and description for the featured reading.",
          "Veuillez indiquer un titre, un auteur et une description pour la lecture à l’honneur."
        )
      );
      return;
    }

    const payload = {
      section: SECTION_FEATURED_READING,
      language: readingForm.language === "fr" ? "fr" : "en",
      displayTitle: safeText(readingForm.displayTitle),
      displayAuthor: safeText(readingForm.displayAuthor),
      hook: safeText(readingForm.hook),
      cta: safeText(
        readingForm.cta,
        readingForm.language === "fr" ? "Découvrir la recommandation" : "Explore the recommendation"
      ),
      displayOrder: Number(readingForm.displayOrder || 1),
      isActive: readingForm.isActive === true,
      publicationUrl: safeText(readingForm.publicationUrl),
      coverOverrideUrl: safeText(readingForm.coverOverrideUrl),
      wisdomText: safeText(readingForm.wisdomText),
      badge: "featured",
    };

    setSavingReading(true);
    try {
      if (isEditingReading && readingForm.id) {
        await api.updateAdminPublicHomepageItem(readingForm.id, payload);
      } else {
        await api.createAdminPublicHomepageItem(payload);
      }

      setNotice(copy.saved);
      await loadReadingItems(languageFilter);
      setIsEditingReading(false);
    } catch (err) {
      setError(err?.message || copy.saveFailed);
    } finally {
      setSavingReading(false);
    }
  }

  async function saveItem(e) {
    e.preventDefault();
    setNotice("");
    setError("");

    const isManualCard = form.isManual === true;

    if (!isManualCard && !form.storyId) {
      setError(copy.selectStoryFirst);
      return;
    }

    if (isManualCard && (!safeText(form.displayTitle) || !safeText(form.hook) || !safeText(form.coverOverrideUrl))) {
      setError(copy.manualCardNeeds);
      return;
    }

    const payload = {
      section: SECTION_FEATURED_STORIES,
      storyId: isManualCard ? "" : form.storyId,
      language: form.language === "fr" ? "fr" : "en",
      displayTitle: safeText(form.displayTitle),
      displayAuthor: safeText(form.displayAuthor),
      hook: safeText(form.hook),
      cta: safeText(form.cta, form.language === "fr" ? "Lire" : "Open story"),
      badge: form.badge || "featured",
      displayOrder: Number(form.displayOrder || 1),
      isActive: form.isActive === true,
      coverOverrideUrl: safeText(form.coverOverrideUrl),
      developmentStatus: form.developmentStatus || "inDevelopment",
      publicationStatus: form.publicationStatus || "notPublished",
      publicationUrl: safeText(form.publicationUrl),
      publicationCta: safeText(form.publicationCta),
    };

    setSaving(true);
    try {
      if (isEditing && form.id) {
        await api.updateAdminPublicHomepageItem(form.id, payload);
      } else {
        await api.createAdminPublicHomepageItem(payload);
      }

      setNotice(copy.saved);
      await loadItems(languageFilter);
      resetForm(languageFilter);
    } catch (err) {
      setError(err?.message || copy.saveFailed);
    } finally {
      setSaving(false);
    }
  }

  async function saveMysteryItem(e) {
    e.preventDefault();
    setNotice("");
    setError("");

    if (!mysteryForm.mysteryId) {
      setError(copy.selectMysteryFirst);
      return;
    }

    const payload = {
      section: SECTION_WEEKLY_MYSTERY,
      mysteryId: mysteryForm.mysteryId,
      language: mysteryForm.language === "fr" ? "fr" : "en",
      displayTitle: safeText(mysteryForm.displayTitle),
      hook: safeText(mysteryForm.hook),
      cta: safeText(
        mysteryForm.cta,
        mysteryForm.language === "fr" ? "Essayez l’énigme de la semaine" : "Try this week's puzzle"
      ),
      badge: mysteryForm.badge || "featured",
      displayOrder: Number(mysteryForm.displayOrder || 1),
      isActive: mysteryForm.isActive === true,
      coverOverrideUrl: safeText(mysteryForm.coverOverrideUrl),
    };

    setSavingMystery(true);
    try {
      if (isEditingMystery && mysteryForm.id) {
        await api.updateAdminPublicHomepageItem(mysteryForm.id, payload);
      } else {
        await api.createAdminPublicHomepageItem(payload);
      }

      setNotice(copy.saved);
      await loadWeeklyItems(languageFilter);
      setIsEditingMystery(false);
    } catch (err) {
      setError(err?.message || copy.saveFailed);
    } finally {
      setSavingMystery(false);
    }
  }

  async function deleteItem(item) {
    const ok = window.confirm(copy.confirmDelete);
    if (!ok) return;

    setNotice("");
    setError("");
    try {
      await api.deleteAdminPublicHomepageItem(item.id);
      setNotice(copy.deleted);
      await loadItems(languageFilter);
      await loadWeeklyItems(languageFilter);
      await loadReadingItems(languageFilter);
      if (form.id && String(form.id) === String(item.id)) resetForm(languageFilter);
      if (mysteryForm.id && String(mysteryForm.id) === String(item.id)) resetMysteryForm(languageFilter);
      if (readingForm.id && String(readingForm.id) === String(item.id)) resetReadingForm(languageFilter);
    } catch (err) {
      setError(err?.message || copy.saveFailed);
    }
  }

  async function toggleActive(item) {
    setNotice("");
    setError("");

    try {
      await api.updateAdminPublicHomepageItem(item.id, {
        isActive: item.isActive !== true,
      });
      await loadItems(languageFilter);
      await loadWeeklyItems(languageFilter);
    } catch (err) {
      setError(err?.message || copy.saveFailed);
    }
  }

  const selectedStory = stories.find((s) => String(s.id) === String(form.storyId));
  const previewCover = form.coverOverrideUrl || getStoryCover(selectedStory);
  const previewTitle = form.displayTitle || selectedStory?.title || "Story title";
  const previewAuthor = form.displayAuthor || selectedStory?.author || "Author";
  const previewHook = form.hook || selectedStory?.description || selectedStory?.synopsis || "";
  const previewCta = form.cta || (form.language === "fr" ? "Lire" : "Open story");

  const selectedMystery = mysteries.find((m) => String(m.id) === String(mysteryForm.mysteryId));
  const mysteryPreviewCover =
    mysteryForm.coverOverrideUrl || selectedMystery?.imageUrl || "/images/mystery250/the-one-way-footprints.png";
  const mysteryPreviewTitle = mysteryForm.displayTitle || selectedMystery?.title || "Mystery title";
  const mysteryPreviewHook =
    mysteryForm.hook || selectedMystery?.teaser || shortQuestion(selectedMystery?.question) || "";
  const mysteryPreviewCta =
    mysteryForm.cta ||
    (mysteryForm.language === "fr" ? "Essayez l’énigme de la semaine" : "Try this week's puzzle");

  const readingPreviewLabel = readingForm.language === "fr" ? "Livre du mois" : "Book of the Month";
  const readingPreviewTitle = readingForm.displayTitle || (readingForm.language === "fr" ? "Les Misérables" : "Anna Karenina");
  const readingPreviewAuthor = readingForm.displayAuthor || (readingForm.language === "fr" ? "Victor Hugo" : "Leo Tolstoy");
  const readingPreviewHook = readingForm.hook || "";
  const readingPreviewWisdom =
    readingForm.wisdomText ||
    (readingForm.language === "fr"
      ? FEATURED_READING_DEFAULTS.fr.wisdomText
      : FEATURED_READING_DEFAULTS.en.wisdomText);
  const readingPreviewCta =
    readingForm.cta ||
    (readingForm.language === "fr" ? "Découvrir la recommandation" : "Explore the recommendation");

  return (
    <main
      style={{
        minHeight: "calc(100vh - 120px)",
        background:
          "linear-gradient(180deg, rgba(250, 247, 238, 0.98), rgba(255, 255, 255, 0.98))",
        padding: "34px 18px 54px",
      }}
    >
      <div style={{ width: "min(1180px, 100%)", margin: "0 auto" }}>
        <section
          style={{
            border: "1px solid rgba(151, 116, 43, 0.18)",
            borderRadius: 28,
            padding: "28px",
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.96), rgba(250,246,233,0.92))",
            boxShadow: "0 20px 55px rgba(23, 31, 25, 0.08)",
            marginBottom: 22,
          }}
        >
          <div style={{ display: "grid", gap: 10 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#9a6a18",
              }}
            >
              {stewardOnly ? "NextScenes Content Steward" : "NextScenes Admin"}
            </div>
            <h1
              style={{
                margin: 0,
                fontSize: "clamp(2rem, 4vw, 3.2rem)",
                lineHeight: 1.03,
                letterSpacing: "-0.055em",
                color: "rgba(15, 36, 24, 0.96)",
              }}
            >
              {stewardOnly ? (lang === "fr" ? "Gestion des contenus" : "Editorial Content") : copy.title}
            </h1>
            <p
              style={{
                margin: 0,
                maxWidth: 760,
                fontSize: "1rem",
                lineHeight: 1.65,
                color: "rgba(15, 36, 24, 0.66)",
              }}
            >
              {stewardOnly ? (lang === "fr" ? "Gérer les lectures à l’honneur et l’énigme hebdomadaire." : "Manage Featured Reading and the weekly Mystery250 selection.") : copy.subtitle}
            </p>
          </div>
        </section>

        {notice ? <div style={noticeStyle("ok")}>{notice}</div> : null}
        {error ? <div style={noticeStyle("bad")}>{error}</div> : null}

        <section style={{ ...panelStyle(), marginBottom: 18 }}>
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              marginBottom: 16,
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: "1.25rem", color: "rgba(15, 36, 24, 0.96)" }}>
                {copy.featuredReading}
              </h2>
              <p style={{ margin: "5px 0 0", color: "rgba(15,36,24,0.62)", lineHeight: 1.5 }}>
                {copy.featuredReadingHelp}
              </p>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <select
                value={languageFilter}
                onChange={(e) => onLanguageFilterChange(e.target.value)}
                style={{ ...fieldStyle(), width: 150, borderRadius: 999, padding: "9px 12px", fontWeight: 800 }}
              >
                {LANGUAGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {pickText(lang, option.en, option.fr)}
                  </option>
                ))}
              </select>

              <button type="button" onClick={() => loadReadingItems(languageFilter)} style={smallButtonStyle()}>
                {loadingReadingItems ? copy.loading : copy.refresh}
              </button>

              <button
                type="button"
                onClick={() => {
                  resetReadingForm(languageFilter);
                  scrollToReadingForm();
                }}
                style={smallButtonStyle({ background: "#12351f", color: "#ffffff", borderColor: "#12351f" })}
              >
                {copy.setReading}
              </button>
            </div>
          </div>

          {loadingReadingItems ? (
            <div style={{ padding: 18, color: "rgba(15, 36, 24, 0.58)" }}>{copy.loading}</div>
          ) : readingItems.length ? (
            <div style={{ display: "grid", gap: 10, marginBottom: 18 }}>
              {readingItems.map((item) => {
                const isCurrentEdit = isEditingReading && String(readingForm.id) === String(item.id);
                return (
                  <article
                    key={item.id}
                    style={{
                      border: isCurrentEdit
                        ? "2px solid rgba(151, 116, 43, 0.42)"
                        : "1px solid rgba(15, 36, 24, 0.1)",
                      borderRadius: 18,
                      padding: 14,
                      background: item.isActive ? "rgba(255,255,255,0.98)" : "rgba(15,36,24,0.035)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
                      <div style={{ minWidth: 0 }}>
                        <h3 style={{ margin: 0, fontSize: "1.08rem", lineHeight: 1.25, color: "rgba(15,36,24,0.94)" }}>
                          {item.displayTitle || "Book title"}
                        </h3>
                        <div style={{ marginTop: 3, fontSize: 13, color: "rgba(15,36,24,0.58)" }}>
                          {item.displayAuthor || "Author"} · {labelForLanguage(item.language, lang)}
                        </div>
                      </div>

                      <span
                        style={{
                          whiteSpace: "nowrap",
                          borderRadius: 999,
                          padding: "4px 8px",
                          fontSize: 11,
                          fontWeight: 900,
                          color: item.isActive ? "#12683b" : "rgba(15,36,24,0.48)",
                          background: item.isActive ? "rgba(20, 138, 74, 0.09)" : "rgba(15,36,24,0.06)",
                        }}
                      >
                        {item.isActive ? copy.itemStatusActive : copy.itemStatusInactive}
                      </span>
                    </div>

                    <p style={{ margin: "8px 0 0", color: "rgba(15,36,24,0.68)", lineHeight: 1.45 }}>
                      {item.hook}
                    </p>

                    <div style={{ marginTop: 10, display: "flex", gap: 7, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => startEditReading(item)}
                        style={smallButtonStyle({ padding: "7px 10px", fontSize: 12 })}
                      >
                        {isCurrentEdit ? copy.updateReading : copy.editAction}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleActive(item)}
                        style={smallButtonStyle({ padding: "7px 10px", fontSize: 12 })}
                      >
                        {item.isActive ? pickText(lang, "Deactivate", "Désactiver") : pickText(lang, "Activate", "Activer")}
                      </button>
                      {!stewardOnly ? (                      <button
                        type="button"
                        onClick={() => deleteItem(item)}
                        style={smallButtonStyle({ padding: "7px 10px", fontSize: 12, color: "#991b1b" })}
                      >
                        {copy.delete}
                      </button>) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div
              style={{
                border: "1px dashed rgba(15, 36, 24, 0.18)",
                borderRadius: 18,
                padding: 18,
                color: "rgba(15,36,24,0.58)",
                background: "rgba(15,36,24,0.025)",
                marginBottom: 18,
              }}
            >
              {copy.noReading}
            </div>
          )}

          <div
            ref={readingFormRef}
            style={{
              border: isEditingReading ? "2px solid rgba(151, 116, 43, 0.34)" : "1px solid rgba(15,36,24,0.08)",
              borderRadius: 20,
              padding: 16,
              background: "rgba(250, 247, 238, 0.42)",
            }}
          >
            <h3 style={{ margin: "0 0 12px", fontSize: "1.08rem", color: "rgba(15,36,24,0.94)" }}>
              {isEditingReading ? copy.updateReading : copy.setReading}
            </h3>

            <form onSubmit={saveReadingItem} style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={labelStyle()}>
                  {copy.language}
                  <select
                    value={readingForm.language}
                    onChange={(e) => onReadingFormLanguageChange(e.target.value)}
                    style={fieldStyle()}
                  >
                    {LANGUAGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {pickText(lang, option.en, option.fr)}
                      </option>
                    ))}
                  </select>
                </label>

                <label style={labelStyle()}>
                  {copy.readingUrl}
                  <input
                    value={readingForm.publicationUrl}
                    onChange={(e) => setReadingForm((prev) => ({ ...prev, publicationUrl: e.target.value }))}
                    placeholder="https://..."
                    style={fieldStyle()}
                  />
                </label>
              </div>

              <CoverImagePicker
                lang={lang}
                value={readingForm.coverOverrideUrl}
                onChange={(url) => setReadingForm((prev) => ({ ...prev, coverOverrideUrl: url }))}
                copy={copy}
              />

              <label style={labelStyle()}>
                {copy.displayTitle}
                <input
                  value={readingForm.displayTitle}
                  onChange={(e) => setReadingForm((prev) => ({ ...prev, displayTitle: e.target.value }))}
                  style={fieldStyle()}
                />
              </label>

              <label style={labelStyle()}>
                {copy.displayAuthor}
                <input
                  value={readingForm.displayAuthor}
                  onChange={(e) => setReadingForm((prev) => ({ ...prev, displayAuthor: e.target.value }))}
                  style={fieldStyle()}
                />
              </label>

              <label style={labelStyle()}>
                {copy.hook}
                <textarea
                  rows={3}
                  value={readingForm.hook}
                  onChange={(e) => setReadingForm((prev) => ({ ...prev, hook: e.target.value }))}
                  style={{ ...fieldStyle(), resize: "vertical", lineHeight: 1.5 }}
                />
              </label>

              <label style={labelStyle()}>
                {copy.readingWisdomText}
                <textarea
                  rows={3}
                  value={readingForm.wisdomText}
                  onChange={(e) => setReadingForm((prev) => ({ ...prev, wisdomText: e.target.value }))}
                  style={{ ...fieldStyle(), resize: "vertical", lineHeight: 1.5 }}
                />
              </label>

              <label style={labelStyle()}>
                {copy.cta}
                <input
                  value={readingForm.cta}
                  onChange={(e) => setReadingForm((prev) => ({ ...prev, cta: e.target.value }))}
                  style={fieldStyle()}
                />
              </label>

              <label style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 14, fontWeight: 800, color: "rgba(15, 36, 24, 0.72)" }}>
                <input
                  type="checkbox"
                  checked={readingForm.isActive === true}
                  onChange={(e) => setReadingForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                />
                {copy.active}
              </label>

              <div style={{ border: "1px solid rgba(151, 116, 43, 0.18)", background: "rgba(255,255,255,0.7)", borderRadius: 18, padding: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9a6a18", marginBottom: 10 }}>
                  {copy.preview}
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: readingForm.coverOverrideUrl ? "86px minmax(0, 1fr)" : "1fr",
                    gap: 12,
                    alignItems: "start",
                  }}
                >
                  {readingForm.coverOverrideUrl ? (
                    <div
                      style={{
                        width: 86,
                        aspectRatio: "2 / 3",
                        borderRadius: 0,
                        overflow: "hidden",
                        background: "rgba(15,36,24,0.08)",
                        border: "1px solid rgba(15,36,24,0.14)",
                        boxShadow: "none",
                      }}
                    >
                      <img
                        src={readingForm.coverOverrideUrl}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                          borderRadius: 0,
                        }}
                      />
                    </div>
                  ) : null}

                  <div style={{ display: "grid", gap: 8 }}>
                    <div style={{ display: "inline-flex", width: "fit-content", borderRadius: 999, padding: "4px 8px", background: "rgba(151, 116, 43, 0.12)", color: "#8a5b11", fontSize: 11, fontWeight: 900 }}>
                      {readingPreviewLabel}
                    </div>
                    <h3 style={{ margin: 0, color: "rgba(15,36,24,0.94)", fontSize: "1rem", lineHeight: 1.25 }}>
                      {readingPreviewTitle}
                    </h3>
                    <div style={{ fontSize: 13, color: "rgba(15,36,24,0.58)", fontWeight: 800 }}>
                      {readingForm.language === "fr" ? "De" : "By"} {readingPreviewAuthor}
                    </div>
                    <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: "rgba(15,36,24,0.68)" }}>
                      {readingPreviewHook}
                    </p>
                    {readingPreviewWisdom ? (
                      <>
                        <div style={{ height: 1, background: "rgba(151,116,43,0.22)", margin: "2px 0" }} />
                        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: "rgba(15,36,24,0.62)", fontStyle: "italic" }}>
                          {readingPreviewWisdom}
                        </p>
                      </>
                    ) : null}
                    <div style={{ marginTop: 2, fontSize: 13, fontWeight: 900, color: "#9a6a18" }}>
                      {readingPreviewCta} →
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 9, flexWrap: "wrap", marginTop: 2 }}>
                <button
                  type="submit"
                  disabled={savingReading}
                  style={smallButtonStyle({ background: savingReading ? "rgba(18,53,31,0.72)" : "#12351f", color: "#ffffff", borderColor: "#12351f", padding: "10px 16px" })}
                >
                  {savingReading ? copy.loading : isEditingReading ? copy.update : copy.save}
                </button>

                <button type="button" onClick={() => resetReadingForm(languageFilter)} style={smallButtonStyle({ padding: "10px 16px" })}>
                  {copy.cancel}
                </button>
              </div>
            </form>
          </div>
        </section>

        <section style={{ ...panelStyle(), marginBottom: 18 }}>
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              marginBottom: 16,
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: "1.25rem", color: "rgba(15, 36, 24, 0.96)" }}>
                {copy.weeklyMystery}
              </h2>
              <p style={{ margin: "5px 0 0", color: "rgba(15,36,24,0.62)", lineHeight: 1.5 }}>
                {copy.weeklyMysteryHelp}
              </p>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <select
                value={languageFilter}
                onChange={(e) => onLanguageFilterChange(e.target.value)}
                style={{ ...fieldStyle(), width: 150, borderRadius: 999, padding: "9px 12px", fontWeight: 800 }}
              >
                {LANGUAGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {pickText(lang, option.en, option.fr)}
                  </option>
                ))}
              </select>

              <button type="button" onClick={() => loadWeeklyItems(languageFilter)} style={smallButtonStyle()}>
                {loadingMysteryItems ? copy.loading : copy.refresh}
              </button>

              <button
                type="button"
                onClick={() => {
                  resetMysteryForm(languageFilter);
                  scrollToMysteryForm();
                }}
                style={smallButtonStyle({ background: "#12351f", color: "#ffffff", borderColor: "#12351f" })}
              >
                {copy.setMystery}
              </button>
            </div>
          </div>

          {loadingMysteryItems ? (
            <div style={{ padding: 18, color: "rgba(15, 36, 24, 0.58)" }}>{copy.loading}</div>
          ) : weeklyItems.length ? (
            <div style={{ display: "grid", gap: 10, marginBottom: 18 }}>
              {weeklyItems.map((item) => {
                const cover = getItemCover(item);
                const title = item.displayTitle || item.mystery?.title || "Untitled Mystery";
                const teaser = item.hook || item.mystery?.question || "";
                const mysteryUrl = makeMysteryUrl(item);
                const isCurrentEdit = isEditingMystery && String(mysteryForm.id) === String(item.id);

                return (
                  <article
                    key={item.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(180px, 340px) minmax(0, 1fr)",
                      gap: 14,
                      border: isCurrentEdit
                        ? "2px solid rgba(151, 116, 43, 0.42)"
                        : "1px solid rgba(15, 36, 24, 0.1)",
                      borderRadius: 18,
                      padding: 12,
                      background: item.isActive ? "rgba(255,255,255,0.98)" : "rgba(15,36,24,0.035)",
                    }}
                  >
                    <div
                      style={{
                        aspectRatio: "16 / 9",
                        borderRadius: 16,
                        overflow: "hidden",
                        background: "rgba(15,36,24,0.08)",
                        border: "1px solid rgba(15,36,24,0.1)",
                      }}
                    >
                      <img src={cover} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    </div>

                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                        <div style={{ minWidth: 0 }}>
                          <h3 style={{ margin: 0, fontSize: "1.08rem", lineHeight: 1.25, color: "rgba(15,36,24,0.94)" }}>
                            {title}
                          </h3>
                          <div style={{ marginTop: 3, fontSize: 13, color: "rgba(15,36,24,0.58)" }}>
                            Mystery250 · {item.mystery?.difficulty || ""} · {labelForLanguage(item.language, lang)}
                          </div>
                        </div>

                        <span
                          style={{
                            whiteSpace: "nowrap",
                            borderRadius: 999,
                            padding: "4px 8px",
                            fontSize: 11,
                            fontWeight: 900,
                            color: item.isActive ? "#12683b" : "rgba(15,36,24,0.48)",
                            background: item.isActive ? "rgba(20, 138, 74, 0.09)" : "rgba(15,36,24,0.06)",
                          }}
                        >
                          {item.isActive ? copy.itemStatusActive : copy.itemStatusInactive}
                        </span>
                      </div>

                      <p style={{ margin: "8px 0 0", color: "rgba(15,36,24,0.68)", lineHeight: 1.45 }}>
                        {teaser}
                      </p>

                      <div style={{ marginTop: 10, display: "flex", gap: 7, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          onClick={() => startEditMystery(item)}
                          style={smallButtonStyle({ padding: "7px 10px", fontSize: 12 })}
                        >
                          {isCurrentEdit ? copy.updateMystery : copy.editAction}
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleActive(item)}
                          style={smallButtonStyle({ padding: "7px 10px", fontSize: 12 })}
                        >
                          {item.isActive ? pickText(lang, "Deactivate", "Désactiver") : pickText(lang, "Activate", "Activer")}
                        </button>
                        {!stewardOnly ? (                        <button
                          type="button"
                          onClick={() => deleteItem(item)}
                          style={smallButtonStyle({ padding: "7px 10px", fontSize: 12, color: "#991b1b" })}
                        >
                          {copy.delete}
                        </button>) : null}
                        {mysteryUrl ? (
                          <a
                            href={mysteryUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              ...smallButtonStyle({ padding: "7px 10px", fontSize: 12, textDecoration: "none", color: "#9a6a18" }),
                              display: "inline-flex",
                            }}
                          >
                            Mystery250
                          </a>
                        ) : null}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div
              style={{
                border: "1px dashed rgba(15, 36, 24, 0.18)",
                borderRadius: 18,
                padding: 18,
                color: "rgba(15,36,24,0.58)",
                background: "rgba(15,36,24,0.025)",
                marginBottom: 18,
              }}
            >
              {copy.noMystery}
            </div>
          )}

          <div
            ref={mysteryFormRef}
            style={{
              border: isEditingMystery ? "2px solid rgba(151, 116, 43, 0.34)" : "1px solid rgba(15,36,24,0.08)",
              borderRadius: 20,
              padding: 16,
              background: "rgba(250, 247, 238, 0.42)",
            }}
          >
            <h3 style={{ margin: "0 0 12px", fontSize: "1.08rem", color: "rgba(15,36,24,0.94)" }}>
              {isEditingMystery ? copy.updateMystery : copy.setMystery}
            </h3>

            <form onSubmit={saveMysteryItem} style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={labelStyle()}>
                  {copy.language}
                  <select
                    value={mysteryForm.language}
                    onChange={(e) => onMysteryFormLanguageChange(e.target.value)}
                    style={fieldStyle()}
                  >
                    {LANGUAGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {pickText(lang, option.en, option.fr)}
                      </option>
                    ))}
                  </select>
                </label>

                <label style={labelStyle()}>
                  {copy.searchMysteries}
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      value={mysteryQuery}
                      onChange={(e) => setMysteryQuery(e.target.value)}
                      placeholder={copy.searchMysteries}
                      style={fieldStyle()}
                    />
                    <button type="button" onClick={() => loadMysteries({ query: mysteryQuery })} style={smallButtonStyle({ borderRadius: 14 })}>
                      {loadingMysteries ? "…" : "Go"}
                    </button>
                  </div>
                </label>
              </div>

              <label style={labelStyle()}>
                {copy.selectMystery}
                <select value={mysteryForm.mysteryId} onChange={(e) => onSelectMystery(e.target.value)} style={fieldStyle()}>
                  <option value="">{copy.selectMystery}</option>
                  {mysteries.map((mystery) => (
                    <option key={mystery.id} value={mystery.id}>
                      {mystery.title || "Untitled Mystery"} · {mystery.difficulty || "Mystery250"}
                    </option>
                  ))}
                </select>
              </label>

              <button
                type="button"
                onClick={fillFromMystery}
                style={smallButtonStyle({ justifySelf: "start", borderColor: "rgba(151, 116, 43, 0.26)", color: "#8a5b11" })}
              >
                {copy.fillEmptyMystery}
              </button>

              <label style={labelStyle()}>
                {copy.displayTitle}
                <input
                  value={mysteryForm.displayTitle}
                  onChange={(e) => setMysteryForm((prev) => ({ ...prev, displayTitle: e.target.value }))}
                  style={fieldStyle()}
                />
              </label>

              <label style={labelStyle()}>
                {copy.hook}
                <textarea
                  rows={3}
                  value={mysteryForm.hook}
                  onChange={(e) => setMysteryForm((prev) => ({ ...prev, hook: e.target.value }))}
                  style={{ ...fieldStyle(), resize: "vertical", lineHeight: 1.5 }}
                />
              </label>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={labelStyle()}>
                  {copy.cta}
                  <input
                    value={mysteryForm.cta}
                    onChange={(e) => setMysteryForm((prev) => ({ ...prev, cta: e.target.value }))}
                    style={fieldStyle()}
                  />
                </label>

                <CoverImagePicker
                  lang={lang}
                  value={mysteryForm.coverOverrideUrl}
                  onChange={(url) => setMysteryForm((prev) => ({ ...prev, coverOverrideUrl: url }))}
                  copy={copy}
                  uploadMode="mystery"
                />
              </div>

              <div style={{ fontSize: 12, color: "rgba(15,36,24,0.52)", lineHeight: 1.45 }}>
                {copy.mysteryManualHint}
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 14, fontWeight: 800, color: "rgba(15, 36, 24, 0.72)" }}>
                <input
                  type="checkbox"
                  checked={mysteryForm.isActive === true}
                  onChange={(e) => setMysteryForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                />
                {copy.active}
              </label>

              <div style={{ border: "1px solid rgba(151, 116, 43, 0.18)", background: "rgba(255,255,255,0.7)", borderRadius: 18, padding: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9a6a18", marginBottom: 10 }}>
                  {copy.preview}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "minmax(150px, 260px) 1fr", gap: 12 }}>
                  <div style={{ aspectRatio: "16 / 9", borderRadius: 14, overflow: "hidden", background: "rgba(15,36,24,0.08)", border: "1px solid rgba(15,36,24,0.1)" }}>
                    <img src={mysteryPreviewCover} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "inline-flex", marginBottom: 6, borderRadius: 999, padding: "4px 8px", background: "rgba(151, 116, 43, 0.12)", color: "#8a5b11", fontSize: 11, fontWeight: 900 }}>
                      {copy.weeklyMystery}
                    </div>
                    <h3 style={{ margin: 0, color: "rgba(15,36,24,0.94)", fontSize: "1rem", lineHeight: 1.25 }}>
                      {mysteryPreviewTitle}
                    </h3>
                    <p style={{ margin: "7px 0 0", fontSize: 13, lineHeight: 1.45, color: "rgba(15,36,24,0.68)" }}>
                      {mysteryPreviewHook}
                    </p>
                    <div style={{ marginTop: 8, fontSize: 13, fontWeight: 900, color: "#9a6a18" }}>
                      {mysteryPreviewCta} →
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 9, flexWrap: "wrap", marginTop: 2 }}>
                <button
                  type="submit"
                  disabled={savingMystery}
                  style={smallButtonStyle({ background: savingMystery ? "rgba(18,53,31,0.72)" : "#12351f", color: "#ffffff", borderColor: "#12351f", padding: "10px 16px" })}
                >
                  {savingMystery ? copy.loading : isEditingMystery ? copy.update : copy.save}
                </button>

                <button type="button" onClick={() => resetMysteryForm(languageFilter)} style={smallButtonStyle({ padding: "10px 16px" })}>
                  {copy.cancel}
                </button>
              </div>
            </form>
          </div>
        </section>

        {!stewardOnly ? (<section
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.05fr) minmax(340px, 0.95fr)",
            gap: 18,
            alignItems: "start",
          }}
        >
          <div style={{ display: "grid", gap: 16 }}>
            <div style={panelStyle()}>
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  marginBottom: 16,
                }}
              >
                <div>
                  <h2 style={{ margin: 0, fontSize: "1.25rem", color: "rgba(15, 36, 24, 0.96)" }}>
                    {copy.featuredStories}
                  </h2>
                  <div style={{ marginTop: 4, fontSize: 13, color: "rgba(15, 36, 24, 0.58)" }}>
                    {labelForLanguage(languageFilter, lang)}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <select
                    value={languageFilter}
                    onChange={(e) => onLanguageFilterChange(e.target.value)}
                    aria-label={copy.language}
                    style={{ ...fieldStyle(), width: 150, borderRadius: 999, padding: "9px 12px", fontWeight: 800 }}
                  >
                    {LANGUAGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {pickText(lang, option.en, option.fr)}
                      </option>
                    ))}
                  </select>

                  <button type="button" onClick={() => loadItems(languageFilter)} style={smallButtonStyle()}>
                    {loadingItems ? copy.loading : copy.refresh}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      resetForm(languageFilter);
                      scrollToStoryForm();
                    }}
                    style={smallButtonStyle({ background: "#12351f", color: "#ffffff", borderColor: "#12351f" })}
                  >
                    {copy.addStory}
                  </button>
                </div>
              </div>

              {loadingItems ? (
                <div style={{ padding: 18, color: "rgba(15, 36, 24, 0.58)" }}>{copy.loading}</div>
              ) : items.length ? (
                <div style={{ display: "grid", gap: 10 }}>
                  {items.map((item) => {
                    const cover = getItemCover(item);
                    const title = item.displayTitle || item.story?.title || "Untitled Story";
                    const author = item.displayAuthor || item.story?.author || "";
                    const readerUrl = makeReaderUrl(item.storyId);
                    const isCurrentEdit = isEditing && String(form.id) === String(item.id);

                    return (
                      <article
                        key={item.id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "58px minmax(0, 1fr)",
                          gap: 12,
                          border: isCurrentEdit ? "2px solid rgba(151, 116, 43, 0.42)" : "1px solid rgba(15, 36, 24, 0.1)",
                          borderRadius: 18,
                          padding: 10,
                          background: item.isActive ? "rgba(255,255,255,0.98)" : "rgba(15,36,24,0.035)",
                        }}
                      >
                        <div style={{ width: 58, height: 78, borderRadius: 12, overflow: "hidden", background: "rgba(15,36,24,0.08)", border: "1px solid rgba(15,36,24,0.1)" }}>
                          <img src={cover} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                        </div>

                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                            <div style={{ minWidth: 0 }}>
                              <h3 style={{ margin: 0, fontSize: "1rem", lineHeight: 1.25, color: "rgba(15,36,24,0.94)" }}>
                                {title}
                              </h3>
                              <div style={{ marginTop: 3, fontSize: 13, color: "rgba(15,36,24,0.58)" }}>
                                {author}
                              </div>
                            </div>

                            <span style={{ whiteSpace: "nowrap", borderRadius: 999, padding: "4px 8px", fontSize: 11, fontWeight: 900, color: item.isActive ? "#12683b" : "rgba(15,36,24,0.48)", background: item.isActive ? "rgba(20, 138, 74, 0.09)" : "rgba(15,36,24,0.06)" }}>
                              {item.isActive ? copy.itemStatusActive : copy.itemStatusInactive}
                            </span>
                          </div>

                          <div style={{ marginTop: 8, display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center", fontSize: 12, color: "rgba(15,36,24,0.56)" }}>
                            <span>#{item.displayOrder}</span>
                            <span>·</span>
                            <span>{labelForBadge(item.badge, lang)}</span>
                            <span>·</span>
                            <span>{labelForLanguage(item.language, lang)}</span>
                            {item.publicationLabel ? (
                              <>
                                <span>·</span>
                                <span style={{ color: item.publicationStatus === "publishedAmazon" ? "#12683b" : "rgba(15,36,24,0.56)", fontWeight: 800 }}>
                                  {item.publicationLabel}
                                </span>
                              </>
                            ) : null}
                            {item.developmentLabel ? (
                              <>
                                <span>·</span>
                                <span>{item.developmentLabel}</span>
                              </>
                            ) : null}
                            {readerUrl ? (
                              <>
                                <span>·</span>
                                <a href={readerUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#9a6a18", fontWeight: 800 }}>
                                  Reader View
                                </a>
                              </>
                            ) : null}
                          </div>

                          <div style={{ marginTop: 10, display: "flex", gap: 7, flexWrap: "wrap" }}>
                            <button type="button" onClick={() => startEdit(item)} style={smallButtonStyle({ padding: "7px 10px", fontSize: 12, background: isCurrentEdit ? "rgba(151,116,43,0.1)" : "#ffffff" })}>
                              {isCurrentEdit ? copy.editStory : copy.editAction}
                            </button>
                            <button type="button" onClick={() => toggleActive(item)} style={smallButtonStyle({ padding: "7px 10px", fontSize: 12 })}>
                              {item.isActive ? pickText(lang, "Deactivate", "Désactiver") : pickText(lang, "Activate", "Activer")}
                            </button>
                            <button type="button" onClick={() => deleteItem(item)} style={smallButtonStyle({ padding: "7px 10px", fontSize: 12, color: "#991b1b" })}>
                              {copy.delete}
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div style={{ border: "1px dashed rgba(15, 36, 24, 0.18)", borderRadius: 18, padding: 18, color: "rgba(15,36,24,0.58)", background: "rgba(15,36,24,0.025)" }}>
                  {copy.noItems}
                </div>
              )}
            </div>
          </div>

          <div
            ref={storyFormRef}
            style={{
              ...panelStyle(),
              border: isEditing ? "2px solid rgba(151, 116, 43, 0.34)" : "1px solid rgba(15, 36, 24, 0.1)",
              position: "sticky",
              top: 92,
            }}
          >
            <h2 style={{ margin: "0 0 8px", fontSize: "1.25rem", color: "rgba(15, 36, 24, 0.96)" }}>
              {isEditing ? copy.editStory : copy.addStory}
            </h2>

            <form onSubmit={saveItem} style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={labelStyle()}>
                  {copy.language}
                  <select value={form.language} onChange={(e) => onFormLanguageChange(e.target.value)} style={fieldStyle()}>
                    {LANGUAGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {pickText(lang, option.en, option.fr)}
                      </option>
                    ))}
                  </select>
                </label>

                <label style={labelStyle()}>
                  {copy.order}
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={form.displayOrder}
                    onChange={(e) => setForm((prev) => ({ ...prev, displayOrder: e.target.value }))}
                    style={fieldStyle()}
                  />
                </label>
              </div>

              <label style={{ display: "flex", alignItems: "flex-start", gap: 9, fontSize: 14, fontWeight: 800, color: "rgba(15, 36, 24, 0.72)", lineHeight: 1.45 }}>
                <input
                  type="checkbox"
                  checked={form.isManual === true}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      isManual: e.target.checked,
                      storyId: e.target.checked ? "" : prev.storyId,
                    }))
                  }
                  style={{ marginTop: 3 }}
                />
                <span>
                  {copy.manualCard}
                  <br />
                  <span style={{ fontSize: 12, fontWeight: 600, color: "rgba(15,36,24,0.52)" }}>
                    {copy.manualCardHelp}
                  </span>
                </span>
              </label>

              {!form.isManual ? (
                <>
                  <label style={labelStyle()}>
                    {copy.storySearch}
                    <div style={{ display: "flex", gap: 8 }}>
                      <input value={storyQuery} onChange={(e) => setStoryQuery(e.target.value)} placeholder={copy.storySearch} style={fieldStyle()} />
                      <button type="button" onClick={() => loadStories({ query: storyQuery, language: form.language || languageFilter })} style={smallButtonStyle({ borderRadius: 14 })}>
                        {loadingStories ? "…" : "Go"}
                      </button>
                    </div>
                  </label>

                  <label style={labelStyle()}>
                    {copy.selectStory}
                    <select value={form.storyId} onChange={(e) => onSelectStory(e.target.value)} style={fieldStyle()}>
                      <option value="">{copy.selectStory}</option>
                      {stories.map((story) => (
                        <option key={story.id} value={story.id}>
                          {story.title} · {story.author || pickText(lang, "Unknown author", "Auteur inconnu")} · {labelForLanguage(story.language, lang)}
                        </option>
                      ))}
                    </select>
                  </label>

                  <button type="button" onClick={fillEmptyFromStory} style={smallButtonStyle({ justifySelf: "start", borderColor: "rgba(151, 116, 43, 0.26)", color: "#8a5b11" })}>
                    {copy.fillEmptyStory}
                  </button>

                  <div style={{ marginTop: -4, fontSize: 12, color: "rgba(15,36,24,0.52)", lineHeight: 1.45 }}>
                    {copy.manualHint}
                  </div>
                </>
              ) : (
                <div style={{ border: "1px solid rgba(151, 116, 43, 0.18)", borderRadius: 16, padding: 12, background: "rgba(151, 116, 43, 0.06)", color: "rgba(15,36,24,0.62)", fontSize: 13, lineHeight: 1.45 }}>
                  {copy.manualCardNeeds}
                </div>
              )}

              <label style={labelStyle()}>
                {copy.displayTitle}
                <input value={form.displayTitle} onChange={(e) => setForm((prev) => ({ ...prev, displayTitle: e.target.value }))} style={fieldStyle()} />
              </label>

              <label style={labelStyle()}>
                {copy.displayAuthor}
                <input value={form.displayAuthor} onChange={(e) => setForm((prev) => ({ ...prev, displayAuthor: e.target.value }))} style={fieldStyle()} />
              </label>

              <label style={labelStyle()}>
                {copy.hook}
                <textarea rows={3} value={form.hook} onChange={(e) => setForm((prev) => ({ ...prev, hook: e.target.value }))} style={{ ...fieldStyle(), resize: "vertical", lineHeight: 1.5 }} />
              </label>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={labelStyle()}>
                  {copy.cta}
                  <input value={form.cta} onChange={(e) => setForm((prev) => ({ ...prev, cta: e.target.value }))} style={fieldStyle()} />
                </label>

                <label style={labelStyle()}>
                  {copy.badge}
                  <select value={form.badge} onChange={(e) => setForm((prev) => ({ ...prev, badge: e.target.value }))} style={fieldStyle()}>
                    {BADGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {pickText(lang, option.en, option.fr)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <CoverImagePicker
                lang={lang}
                value={form.coverOverrideUrl}
                onChange={(url) => setForm((prev) => ({ ...prev, coverOverrideUrl: url }))}
                copy={copy}
              />

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={labelStyle()}>
                  {copy.developmentStatus}
                  <select value={form.developmentStatus || "inDevelopment"} onChange={(e) => setForm((prev) => ({ ...prev, developmentStatus: e.target.value }))} style={fieldStyle()}>
                    {DEVELOPMENT_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {pickText(lang, option.en, option.fr)}
                      </option>
                    ))}
                  </select>
                </label>

                <label style={labelStyle()}>
                  {copy.publicationStatus}
                  <select value={form.publicationStatus || "notPublished"} onChange={(e) => setForm((prev) => ({ ...prev, publicationStatus: e.target.value }))} style={fieldStyle()}>
                    {PUBLICATION_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {pickText(lang, option.en, option.fr)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label style={labelStyle()}>
                {copy.publicationUrl}
                <input value={form.publicationUrl || ""} onChange={(e) => setForm((prev) => ({ ...prev, publicationUrl: e.target.value }))} placeholder="https://www.amazon.com/..." style={fieldStyle()} />
              </label>

              <label style={labelStyle()}>
                {copy.publicationCta}
                <input value={form.publicationCta || ""} onChange={(e) => setForm((prev) => ({ ...prev, publicationCta: e.target.value }))} placeholder={form.language === "fr" ? "Voir sur Amazon" : "View on Amazon"} style={fieldStyle()} />
              </label>

              <label style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 14, fontWeight: 800, color: "rgba(15, 36, 24, 0.72)" }}>
                <input type="checkbox" checked={form.isActive === true} onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))} />
                {copy.active}
              </label>

              <div style={{ border: "1px solid rgba(151, 116, 43, 0.18)", background: "rgba(250, 246, 233, 0.65)", borderRadius: 18, padding: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9a6a18", marginBottom: 10 }}>
                  {copy.preview}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "70px 1fr", gap: 12 }}>
                  <div style={{ width: 70, height: 94, borderRadius: 14, overflow: "hidden", background: "rgba(15,36,24,0.08)", border: "1px solid rgba(15,36,24,0.1)" }}>
                    <img src={previewCover} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "inline-flex", marginBottom: 6, borderRadius: 999, padding: "4px 8px", background: "rgba(151, 116, 43, 0.12)", color: "#8a5b11", fontSize: 11, fontWeight: 900 }}>
                      {labelForBadge(form.badge, lang)}
                    </div>
                    <h3 style={{ margin: 0, color: "rgba(15,36,24,0.94)", fontSize: "1rem", lineHeight: 1.25 }}>
                      {previewTitle}
                    </h3>
                    <div style={{ marginTop: 3, fontSize: 13, color: "rgba(15,36,24,0.58)" }}>
                      {previewAuthor}
                    </div>
                    <p style={{ margin: "7px 0 0", fontSize: 13, lineHeight: 1.45, color: "rgba(15,36,24,0.68)" }}>
                      {previewHook}
                    </p>
                    <div style={{ marginTop: 8, fontSize: 13, fontWeight: 900, color: "#9a6a18" }}>
                      {previewCta} →
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 9, flexWrap: "wrap", marginTop: 2 }}>
                <button type="submit" disabled={saving} style={smallButtonStyle({ background: saving ? "rgba(18,53,31,0.72)" : "#12351f", color: "#ffffff", borderColor: "#12351f", padding: "10px 16px" })}>
                  {saving ? copy.loading : isEditing ? copy.update : copy.save}
                </button>

                <button type="button" onClick={() => resetForm(languageFilter)} style={smallButtonStyle({ padding: "10px 16px" })}>
                  {copy.cancel}
                </button>
              </div>
            </form>
          </div>
        </section>) : null}
      </div>
    </main>
  );
}
